import { useEffect, useRef, useState } from "react";

interface FirstResolveLatchInput {
  /** Board, token and user are all present, so the fetch key is non-null. */
  isContextReady: boolean;
  isLoading: boolean;
  /** Any truthy error. An errored, dataless load is the error UI's to show, not the loader's. */
  error: unknown;
  /** The board the page is showing, or null before context resolves. */
  currentBoard: number | null | undefined;
}

/**
 * "Has this board resolved at least once?" — a LATCH, not a recurring predicate.
 *
 * The initial load is a one-time event per board. Encoding it as `if (isLoading)` makes a
 * full-page loader flash back on any later blip: a reconnect revalidation, a remount with a
 * cold SWR cache, the token re-fetch effect briefly nulling the key. So the first resolution
 * is remembered, and background reloads keep the rendered data on screen.
 *
 * A board is "resolved" once context is ready, the first fetch is done, and there is no error
 * — empty boards included, since `isLoading` goes false with context ready. The latch resets
 * only on a genuine board switch (to a different, non-null board) so a new board gets its own
 * first load; a board going null (context re-resolving) does not reset it.
 */
const useFirstResolveLatch = ({
  isContextReady,
  isLoading,
  error,
  currentBoard,
}: FirstResolveLatchInput): boolean => {
  const [hasResolvedOnce, setHasResolvedOnce] = useState(false);
  const resolvedBoardRef = useRef<number | null>(null);

  useEffect(() => {
    if (
      currentBoard != null &&
      resolvedBoardRef.current != null &&
      currentBoard !== resolvedBoardRef.current
    ) {
      resolvedBoardRef.current = null;
      setHasResolvedOnce(false);
      return;
    }
    if (isContextReady && !isLoading && !error && currentBoard != null) {
      resolvedBoardRef.current = currentBoard;
      setHasResolvedOnce(true);
    }
  }, [isContextReady, isLoading, error, currentBoard]);

  return hasResolvedOnce;
};

export default useFirstResolveLatch;
